// DefaultViewer — the source viewer for every kind the app can't show as it
// is (E-13, the default case): Word documents and presentations (only their
// text was read), a PDF whose original isn't stored, any unknown kind. No
// preview is faked: one card names the file, says so, and keeps the text that
// was read one click away, marked as read by the assistant (the references
// point to it), with the original to download.
//
// A content whose stored text is gone falls to the reading floor, which
// explains it and offers to upload the original again.

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { LoadedSource } from "../../app/ports/resource.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { fileKindIcon, useFileKindLabel } from "../KnowledgeBank/fileKind.js";
import type { FileKind } from "../KnowledgeBank/knowledgeBankModel.js";
import type { SourceOriginal } from "../SourceView/types.js";
import { Text } from "../Text/Text.js";
import { formatSize } from "./sourceViewerModel.js";

export const DefaultViewer = ({
    fileKind,
    content,
    original,
    filename,
    renderText,
    onReupload,
}: {
    readonly fileKind: FileKind;
    readonly content: LoadedSource["content"];
    readonly original: SourceOriginal | undefined;
    readonly filename: string;
    // The app's reading surface for the read text (marks included).
    readonly renderText: (content: LoadedSource["content"]) => ReactNode;
    readonly onReupload?: () => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const kindLabel = useFileKindLabel();
    // View-ephemeral: whether the read text is unfolded.
    const [showText, setShowText] = useState(false);

    // Nothing was read, or its stored text is gone: the reading floor says
    // which, and offers what can be done.
    if (content.type === "unavailable") return renderText(content);

    const advice =
        fileKind === "presentation"
            ? t`For a full preview, export it as PDF and upload it again.`
            : fileKind === "word"
              ? t`For a full preview, save it as PDF and upload it again.`
              : t`For a full preview, upload it as a PDF.`;

    if (showText)
        return (
            <div className="sv-default--text">
                <div className="sv-default__bar">
                    <AiMarker variant="tag" label={t`Read as text`} />
                    <Text as="span" size="xs" tone="dim">
                        {t`Formatting, images and layout are missing.`}
                    </Text>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowText(false)}
                    >
                        {t`Hide the text`}
                    </Button>
                </div>
                {renderText(content)}
            </div>
        );

    const meta = [
        kindLabel(fileKind),
        original === undefined
            ? undefined
            : formatSize(original.size, i18n.locale),
    ]
        .filter((part) => part !== undefined)
        .join(" · ");

    return (
        <div className="sv-default">
            <div className="sv-default__card">
                <div className="sv-default__file">
                    <span className="sv-default__icon" aria-hidden="true">
                        <Icon name={fileKindIcon(fileKind)} size="md" />
                    </span>
                    <span className="sv-default__name">
                        <Text
                            as="span"
                            size="md"
                            weight="semibold"
                            className="sv-default__filename"
                        >
                            {filename}
                        </Text>
                        <Text as="span" size="sm" tone="dim">
                            {meta}
                        </Text>
                    </span>
                </div>
                <Text as="p" size="lg" weight="medium">
                    {t`We can't show a preview of this file type.`}
                </Text>
                <div className="sv-default__read">
                    <AiMarker variant="tag" label={t`Read as text`} />
                    <Text as="p" size="sm" tone="dim">
                        {t`Its text could be read. Formatting, images and layout are missing. References point to this text.`}
                    </Text>
                    <button
                        type="button"
                        className="sv-default__show"
                        onClick={() => setShowText(true)}
                    >
                        {t`Show the text`}
                        <Icon name="arrow-right" size="sm" />
                    </button>
                </div>
                <Text as="p" size="sm" tone="dim">
                    {advice}
                </Text>
                <div>
                    {original === undefined ? (
                        onReupload === undefined ? null : (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onReupload}
                            >
                                {t`Upload the original again`}
                                <Icon name="upload" size="sm" />
                            </Button>
                        )
                    ) : (
                        <a
                            className="bilda-btn bilda-btn--secondary bilda-btn--sm sv-default__download"
                            href={original.url}
                            download={filename}
                        >
                            {t`Download`}
                            <Icon name="download" size="sm" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};
